import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { History, Search, Unlink, RefreshCw, Download } from 'lucide-react';

const HistorialConciliacionesPage = () => {
  const { empresaActual } = useApp();
  const [loading, setLoading] = useState(false);
  const [historial, setHistorial] = useState([]);
  
  // Filtros - Por defecto mostrar último año
  const [fechaDesde, setFechaDesde] = useState('2024-01-01');
  const [fechaHasta, setFechaHasta] = useState('2026-12-31');
  const [busqueda, setBusqueda] = useState('');
  
  // Modal confirmar romper conciliación
  const [alertRomperOpen, setAlertRomperOpen] = useState(false);
  const [conciliacionARomper, setConciliacionARomper] = useState(null);
  
  // Ordenamiento
  const [sortColumn, setSortColumn] = useState('fecha_mov');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Anchos de columnas
  const [columnWidths, setColumnWidths] = useState({
    fecha: 100,
    banco: 80,
    cuenta: 150,
    nro_op: 90,
    desc_banco: 180,
    monto_banco: 110,
    nro_doc: 100,
    proveedor: 150,
    detalle: 120,
    desc_sistema: 150,
    monto_sistema: 110,
    estado: 80,
  });

  // Cargar historial
  const cargarHistorial = useCallback(async () => {
    if (!empresaActual?.id) return;
    
    setLoading(true);
    try {
      const response = await api.get('/conciliacion/historial', {
        params: { empresa_id: empresaActual.id }
      });
      setHistorial(response.data);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id]);

  useEffect(() => {
    cargarHistorial();
  }, [cargarHistorial]);

  // Romper conciliación
  const handleRomperConciliacion = (conciliacion) => {
    setConciliacionARomper(conciliacion);
    setAlertRomperOpen(true);
  };

  const confirmarRomperConciliacion = async () => {
    if (!conciliacionARomper) return;
    
    try {
      await api.post(`/conciliacion/${conciliacionARomper.id}/romper`);
      toast.success('Conciliación revertida correctamente');
      setAlertRomperOpen(false);
      setConciliacionARomper(null);
      cargarHistorial();
    } catch (error) {
      toast.error('Error al romper conciliación: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Ordenamiento
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Filtrar y ordenar historial
  const historialFiltrado = historial
    .filter(c => {
      // Filtro por búsqueda
      if (busqueda) {
        const search = busqueda.toLowerCase();
        return (
          c.descripcion_banco?.toLowerCase().includes(search) ||
          c.proveedor_beneficiario?.toLowerCase().includes(search) ||
          c.nro_doc?.toLowerCase().includes(search) ||
          c.banco?.toLowerCase().includes(search) ||
          c.nro_op?.toLowerCase().includes(search)
        );
      }
      return true;
    })
    .filter(c => {
      // Filtro por fechas
      if (fechaDesde && c.fecha_mov < fechaDesde) return false;
      if (fechaHasta && c.fecha_mov > fechaHasta) return false;
      return true;
    })
    .sort((a, b) => {
      let valA = a[sortColumn];
      let valB = b[sortColumn];
      
      if (valA === null || valA === undefined) valA = '';
      if (valB === null || valB === undefined) valB = '';
      
      if (typeof valA === 'number' && typeof valB === 'number') {
        return sortDirection === 'asc' ? valA - valB : valB - valA;
      }
      
      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();
      return sortDirection === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
    });

  // Manejar redimensionamiento de columna
  const handleColumnResize = (columnKey, startX, startWidth) => {
    const handleMouseMove = (e) => {
      const diff = e.clientX - startX;
      const newWidth = Math.max(60, startWidth + diff);
      setColumnWidths(prev => ({ ...prev, [columnKey]: newWidth }));
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Header con ordenamiento y resize
  const SortableResizableHeader = ({ columnKey, label, sortKey, width, className = '' }) => (
    <TableHead 
      className={`relative select-none ${className}`}
      style={{ width: width, minWidth: width }}
    >
      <div 
        className="flex items-center gap-1 cursor-pointer hover:text-indigo-600"
        onClick={() => handleSort(sortKey)}
      >
        <span className="truncate">{label}</span>
        {sortColumn === sortKey && (
          <span className="text-indigo-600 font-bold">
            {sortDirection === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
      <div
        className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-indigo-400 active:bg-indigo-600"
        onMouseDown={(e) => {
          e.preventDefault();
          handleColumnResize(columnKey, e.clientX, width);
        }}
      />
    </TableHead>
  );

  // Formatear monto
  const formatMonto = (monto) => {
    const num = parseFloat(monto) || 0;
    return num.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Calcular totales
  const totalMontoBanco = historialFiltrado.reduce((sum, c) => sum + (parseFloat(c.monto_banco) || 0), 0);
  const totalMontoSistema = historialFiltrado.reduce((sum, c) => sum + (parseFloat(c.monto_sistema) || 0), 0);

  return (
    <div className="space-y-6" data-testid="historial-conciliaciones-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <History className="w-7 h-7 text-indigo-600" />
            Historial de Conciliaciones
          </h1>
          <p className="text-gray-500 text-sm mt-1">Vista general de todas las conciliaciones realizadas</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => {
              const params = new URLSearchParams();
              if (empresaActual?.id) params.append('empresa_id', empresaActual.id);
              if (fechaDesde) params.append('fecha_desde', fechaDesde);
              if (fechaHasta) params.append('fecha_hasta', fechaHasta);
              window.open(`${process.env.REACT_APP_BACKEND_URL}/api/conciliacion/historial/export/excel?${params}`, '_blank');
            }}
          >
            <Download className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Button 
            variant="outline"
            onClick={() => {
              const params = new URLSearchParams();
              if (empresaActual?.id) params.append('empresa_id', empresaActual.id);
              if (fechaDesde) params.append('fecha_desde', fechaDesde);
              if (fechaHasta) params.append('fecha_hasta', fechaHasta);
              window.open(`${process.env.REACT_APP_BACKEND_URL}/api/conciliacion/historial/export/pdf?${params}`, '_blank');
            }}
          >
            <Download className="w-4 h-4 mr-2" />
            PDF
          </Button>
          <Button variant="outline" onClick={cargarHistorial} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label>Buscar</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input 
                  placeholder="Descripción, proveedor, nro doc..." 
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label>Desde</Label>
              <Input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Hasta</Label>
              <Input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="mt-1" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resumen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-indigo-50 to-white border-indigo-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Total Conciliaciones</p>
            <p className="text-2xl font-bold text-indigo-600">{historialFiltrado.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Total Monto Banco</p>
            <p className="text-xl font-bold text-blue-600">S/. {formatMonto(totalMontoBanco)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Total Monto Sistema</p>
            <p className="text-xl font-bold text-emerald-600">S/. {formatMonto(totalMontoSistema)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Diferencia</p>
            <p className="text-xl font-bold text-purple-600">S/. {formatMonto(Math.abs(totalMontoBanco - totalMontoSistema))}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabla */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Detalle de Conciliaciones</CardTitle>
            <p className="text-sm text-gray-500">
              Click en columna para ordenar • Arrastre borde para redimensionar
            </p>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <SortableResizableHeader columnKey="fecha" label="Fecha" sortKey="fecha_mov" width={columnWidths.fecha} />
                  <SortableResizableHeader columnKey="banco" label="Banco" sortKey="banco" width={columnWidths.banco} />
                  <SortableResizableHeader columnKey="cuenta" label="Cuenta" sortKey="cuenta_nombre" width={columnWidths.cuenta} />
                  <SortableResizableHeader columnKey="nro_op" label="Nro Op." sortKey="nro_op" width={columnWidths.nro_op} />
                  <SortableResizableHeader columnKey="desc_banco" label="Descripción Banco" sortKey="descripcion_banco" width={columnWidths.desc_banco} />
                  <SortableResizableHeader columnKey="monto_banco" label="Monto Banco" sortKey="monto_banco" width={columnWidths.monto_banco} className="text-right" />
                  <SortableResizableHeader columnKey="nro_doc" label="Nro Doc" sortKey="nro_doc" width={columnWidths.nro_doc} />
                  <SortableResizableHeader columnKey="proveedor" label="Proveedor/Beneficiario" sortKey="proveedor_beneficiario" width={columnWidths.proveedor} />
                  <SortableResizableHeader columnKey="detalle" label="Detalle Sistema" sortKey="detalle_sistema" width={columnWidths.detalle} />
                  <SortableResizableHeader columnKey="desc_sistema" label="Descripción Sistema" sortKey="descripcion_sistema" width={columnWidths.desc_sistema} />
                  <SortableResizableHeader columnKey="monto_sistema" label="Monto Sistema" sortKey="monto_sistema" width={columnWidths.monto_sistema} className="text-right" />
                  <SortableResizableHeader columnKey="estado" label="Estado" sortKey="estado" width={columnWidths.estado} />
                  <TableHead style={{ width: 50 }}></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historialFiltrado.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} className="text-center text-gray-400 py-8">
                      {loading ? 'Cargando...' : 'No hay conciliaciones que mostrar'}
                    </TableCell>
                  </TableRow>
                ) : historialFiltrado.map((c) => (
                  <TableRow key={c.id} className="hover:bg-gray-50">
                    <TableCell style={{ width: columnWidths.fecha }} className="text-sm font-medium">{c.fecha_mov}</TableCell>
                    <TableCell style={{ width: columnWidths.banco }}>
                      <Badge variant="outline" className="text-xs">{c.banco || 'N/A'}</Badge>
                    </TableCell>
                    <TableCell style={{ width: columnWidths.cuenta }} className="text-sm truncate" title={c.cuenta_nombre}>
                      {c.cuenta_nombre || '-'}
                    </TableCell>
                    <TableCell style={{ width: columnWidths.nro_op }} className="text-xs font-mono text-gray-600">{c.nro_op || '-'}</TableCell>
                    <TableCell style={{ width: columnWidths.desc_banco }} className="text-sm truncate" title={c.descripcion_banco}>
                      {c.descripcion_banco}
                    </TableCell>
                    <TableCell style={{ width: columnWidths.monto_banco }} className={`text-right font-mono ${c.monto_banco >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatMonto(c.monto_banco)}
                    </TableCell>
                    <TableCell style={{ width: columnWidths.nro_doc }} className="text-sm font-mono text-gray-600">
                      {c.nro_doc || '-'}
                    </TableCell>
                    <TableCell style={{ width: columnWidths.proveedor }} className="text-sm truncate" title={c.proveedor_beneficiario}>
                      {c.proveedor_beneficiario || '-'}
                    </TableCell>
                    <TableCell style={{ width: columnWidths.detalle }} className="text-sm text-gray-500 truncate">
                      {c.detalle_sistema || '-'}
                    </TableCell>
                    <TableCell style={{ width: columnWidths.desc_sistema }} className="text-sm truncate" title={c.descripcion_sistema}>
                      {c.descripcion_sistema || <span className="text-gray-400">Sin vincular</span>}
                    </TableCell>
                    <TableCell style={{ width: columnWidths.monto_sistema }} className="text-right font-mono">
                      {c.monto_sistema ? formatMonto(c.monto_sistema) : '-'}
                    </TableCell>
                    <TableCell style={{ width: columnWidths.estado }}>
                      <Badge className={
                        c.estado === 'OK' ? 'bg-emerald-100 text-emerald-700' :
                        c.estado === 'PARCIAL' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-600'
                      }>{c.estado}</Badge>
                    </TableCell>
                    <TableCell style={{ width: 50 }}>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleRomperConciliacion(c)}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50"
                        title="Romper conciliación"
                      >
                        <Unlink className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* AlertDialog para confirmar romper conciliación */}
      <AlertDialog open={alertRomperOpen} onOpenChange={setAlertRomperOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Unlink className="w-5 h-5" />
              ¿Romper esta conciliación?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Esta acción revertirá la conciliación y el movimiento bancario volverá a estar pendiente.</p>
              {conciliacionARomper && (
                <div className="bg-gray-50 p-3 rounded-lg mt-3 text-sm">
                  <p><strong>Fecha:</strong> {conciliacionARomper.fecha_mov}</p>
                  <p><strong>Banco:</strong> {conciliacionARomper.banco}</p>
                  <p><strong>Cuenta:</strong> {conciliacionARomper.cuenta_nombre}</p>
                  <p><strong>Descripción:</strong> {conciliacionARomper.descripcion_banco}</p>
                  <p><strong>Monto:</strong> S/. {formatMonto(conciliacionARomper.monto_banco)}</p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConciliacionARomper(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmarRomperConciliacion}
              className="bg-red-600 hover:bg-red-700"
            >
              Sí, romper conciliación
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HistorialConciliacionesPage;
